import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

import {
  getSubscriptionStatus,
  createCheckoutSession,
  createPortalSession,
  cancelSubscription,
  reactivateSubscription,
  getSubscriptionPlans
} from '../services/api/subscriptionService';

const SubscriptionManagement = () => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Load subscription status and available plans on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load subscription status and available plans in parallel
      const [subscriptionData, plans] = await Promise.all([
        getSubscriptionStatus(),
        getSubscriptionPlans()
      ]);

      setSubscription(subscriptionData);
      setAvailablePlans(plans);

      // Pre-select first plan if available
      if (plans.length > 0 && !selectedPlan) {
        setSelectedPlan(plans[0]);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!selectedPlan) {
      setError('Please select a subscription plan');
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      const currentUrl = window.location.origin;
      const { url } = await createCheckoutSession(
        selectedPlan.priceId,
        `${currentUrl}/subscription/success`,
        `${currentUrl}/subscription/cancel`
      );

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (err) {
      console.error('Checkout failed:', err);
      setError(err.response?.data?.error || err.message);
      setActionLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      setActionLoading(true);
      setError(null);

      const currentUrl = window.location.origin;
      const { url } = await createPortalSession(`${currentUrl}/`);

      // Redirect to Stripe Customer Portal
      window.location.href = url;
    } catch (err) {
      console.error('Portal failed:', err);
      setError(err.response?.data?.error || err.message);
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Cancel subscription at end of billing period?')) {
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      await cancelSubscription(subscription.subscription_id);
      await loadData(); // Reload to show updated status
    } catch (err) {
      console.error('Cancel failed:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setActionLoading(true);
      setError(null);

      await reactivateSubscription(subscription.subscription_id);
      await loadData(); // Reload to show updated status
    } catch (err) {
      console.error('Reactivate failed:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'canceled':
        return 'warning';
      case 'past_due':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Subscription Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!subscription ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              No Active Subscription
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              You don't have an active subscription. Create one to access premium features.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CreditCardIcon />}
              onClick={() => setShowCheckoutDialog(true)}
              disabled={actionLoading}
            >
              Subscribe Now
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {/* Subscription Status Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Subscription Status
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={subscription.status.toUpperCase()}
                    color={getStatusColor(subscription.status)}
                    icon={subscription.status === 'active' ? <CheckCircleIcon /> : <CancelIcon />}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  <strong>Subscription ID:</strong> {subscription.subscription_id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Current Period:</strong> {formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}
                </Typography>
                {subscription.cancel_at_period_end && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Subscription will cancel at end of period
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Actions Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Actions
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<CreditCardIcon />}
                    onClick={handleManageBilling}
                    disabled={actionLoading}
                  >
                    Manage Billing
                  </Button>
                  {subscription.cancel_at_period_end ? (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<RefreshIcon />}
                      onClick={handleReactivate}
                      disabled={actionLoading}
                    >
                      Reactivate Subscription
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={handleCancelSubscription}
                      disabled={actionLoading}
                    >
                      Cancel Subscription
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Checkout Dialog */}
      <Dialog open={showCheckoutDialog} onClose={() => setShowCheckoutDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Choose a Subscription Plan</DialogTitle>
        <DialogContent>
          {availablePlans.length === 0 ? (
            <Alert severity="info">
              No subscription plans available. Please contact your administrator to configure subscription plans.
            </Alert>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {availablePlans.map((plan) => (
                <Grid item xs={12} sm={6} key={plan.priceId}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: selectedPlan?.priceId === plan.priceId ? '2px solid' : '1px solid',
                      borderColor: selectedPlan?.priceId === plan.priceId ? 'primary.main' : 'divider',
                      '&:hover': { borderColor: 'primary.main' }
                    }}
                    onClick={() => setSelectedPlan(plan)}
                  >
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {plan.name}
                      </Typography>
                      <Typography variant="h4" color="primary" gutterBottom>
                        ${plan.price}
                        <Typography component="span" variant="subtitle1" color="text.secondary">
                          /{plan.interval}
                        </Typography>
                      </Typography>
                      {plan.description && (
                        <Typography variant="body2" color="text.secondary">
                          {plan.description}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCheckoutDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCheckout}
            variant="contained"
            disabled={!selectedPlan || actionLoading || availablePlans.length === 0}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Continue to Checkout'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionManagement;
